/** Query keys and the handful of reads that more than one screen makes. */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { METAL_TOKEN, type MetalKey as CatalogueMetal } from '@metal/shared'
import { api } from '@metal/ui/api'
import type { MetalKey, TickerItem } from '@metal/ui'

export const qk = {
  me: ['me'] as const,
  ticker: ['ticker'] as const,
  clock: ['clock'] as const,
  catalog: ['catalog'] as const,
  specForm: (productId: string) => ['catalog', 'spec-form', productId] as const,
  productTypes: ['catalog', 'product-types'] as const,
  board: (query: Record<string, unknown>) => ['board', query] as const,
  rfqBoard: (query: Record<string, unknown>) => ['rfq-board', query] as const,
  offer: (id: string | number) => ['offer', String(id)] as const,
  offerBook: (id: string | number) => ['offer', String(id), 'book'] as const,
  rfq: (id: string | number) => ['rfq', String(id)] as const,
  myOffers: (query: Record<string, unknown>) => ['my-offers', query] as const,
  myRfqs: (query: Record<string, unknown>) => ['my-rfqs', query] as const,
  myBids: (query: Record<string, unknown>) => ['my-bids', query] as const,
  bidHistory: (id: string | number) => ['bid', String(id), 'history'] as const,
  deals: (query: Record<string, unknown>) => ['deals', query] as const,
  deal: (id: string | number) => ['deal', String(id)] as const,
  dealDocuments: (id: string | number) => ['deal', String(id), 'documents'] as const,
  negotiation: (id: string | number) => ['negotiation', String(id)] as const,
  dashboard: ['dashboard'] as const,
  notifications: (query: Record<string, unknown>) => ['notifications', query] as const,
  documents: (query: Record<string, unknown>) => ['documents', query] as const,
  wallet: ['wallet'] as const,
  grade: ['grade'] as const,
  news: (query: Record<string, unknown>) => ['news', query] as const,
  newsArticle: (slug: string) => ['news', slug] as const,
  prices: (query: Record<string, unknown>) => ['prices', query] as const,
  marketNav: ['market', 'nav'] as const,
  onboarding: ['onboarding'] as const,
}

export interface TickerPayload {
  variant: string
  items: TickerItem[]
  market?: { stampFa?: string }
}

export interface PublicTickerPayload {
  items: TickerItem[]
  fetchedAtLabel: string
}

export function useTicker() {
  return useQuery({
    queryKey: qk.ticker,
    queryFn: () => api.get<TickerPayload>('/market/ticker'),
    staleTime: 10 * 60_000,
  })
}

/** The public strip, which rides along on the landing shell payload. */
export function usePublicTicker() {
  return useQuery({
    queryKey: ['public-shell'],
    queryFn: () => api.get<{ ticker: PublicTickerPayload }>('/public/shell'),
    staleTime: 10 * 60_000,
    select: (d) => d.ticker,
  })
}

interface ProductTypeRow {
  slug: string
  metal: CatalogueMetal
}

/** The metal swatch, by catalogue slug. */
export function useMetalBySlug(): (slug: string | null | undefined) => MetalKey | undefined {
  const q = useQuery({
    queryKey: qk.productTypes,
    queryFn: () => api.get<{ items: ProductTypeRow[] }>('/catalog/product-types'),
    staleTime: 60 * 60_000,
  })

  return useMemo(() => {
    const byslug = new Map<string, MetalKey>()
    for (const row of q.data?.items ?? []) {
      const token = METAL_TOKEN[row.metal]
      if (token) byslug.set(row.slug, token as MetalKey)
    }
    return (slug) => (slug ? byslug.get(slug) : undefined)
  }, [q.data])
}
